/**
 * Passar a conversa para outro vendedor.
 *
 * ── Quem aparece na lista são MEMBROS do CliqCRM, e não agentes do motor ──
 *
 * A diferença não é vocabulário. "Vendedor" é conceito daqui: tem papel,
 * carteira e escopo (B16), e é o que decide o que ele enxerga depois de
 * receber a conversa. "Agente" é o reflexo dessa pessoa no motor de conversa —
 * como o `agentes.ts` do adapter já dizia.
 *
 * Listar agentes seria mais fácil e estaria errado: passaria a conversa para
 * alguém que o CRM não conhece, e a fila mostraria um nome sem pessoa por trás.
 *
 * ── E por isso a lista tem gente que NÃO pode receber ─────────────────────
 *
 * Um membro só recebe se tiver reflexo nas duas engines:
 *
 *   · no motor de conversa, senão a atribuição é aceita e ignorada — medido:
 *     `assignee_id` que não é membro da caixa devolve 200 e não faz nada;
 *   · no motor de CRM, senão o cliente e a oportunidade não têm para onde ir.
 *
 * Quem não tem vem na lista com `pode_receber: false` e o motivo por escrito.
 * Esconder essas pessoas responderia "não existe" a quem pergunta "cadê o
 * Fulano?" — e a resposta certa é "existe, e falta isto".
 *
 * ── A transferência é TUDO ou NADA ────────────────────────────────────────
 *
 * Ela move três coisas: a conversa, o cliente e a oportunidade. Se qualquer
 * uma não puder ir, nenhuma vai — a conferência acontece antes de a primeira
 * escrita sair.
 *
 * Meia transferência é pior que nenhuma: o WhatsApp apontando para um vendedor
 * e o funil para outro é exatamente o estado que ninguém percebe até o cliente
 * cobrar resposta de quem já não é dono.
 */

import { z } from 'zod';

export const MotivoDeNaoReceberSchema = z.enum([
  /** Sem agente no motor de conversa: a atribuição seria aceita e ignorada. */
  'sem_acesso_ao_atendimento',
  /** Sem membro no motor de CRM: o cliente e a oportunidade não teriam dono. */
  'sem_cadastro_no_crm',
  /** Vínculo suspenso — a pessoa existe e não está trabalhando. */
  'vinculo_suspenso',
]);

export const ResponsavelPossivelSchema = z
  .object({
    /** O id do usuário no CliqCRM. É por ele que a transferência é pedida. */
    usuario_id: z.string().uuid(),
    nome: z.string(),
    papel: z.string(),
    pode_receber: z.boolean(),
    /** `null` quando pode receber. Preenchido é o que a tela mostra ao lado do nome. */
    motivo: MotivoDeNaoReceberSchema.nullable(),
    /** `true` para quem já é o responsável — a tela marca em vez de oferecer de novo. */
    atual: z.boolean(),
  })
  .strict();

export const ResponsaveisPossiveisSchema = z
  .object({
    itens: z.array(ResponsavelPossivelSchema),
  })
  .strict();

export const TransferirConversaSchema = z
  .object({
    /**
     * Para quem vai. `null` desatribui.
     *
     * Desatribuir devolve a conversa à fila sem dono, e NÃO mexe no cliente
     * nem na oportunidade: "ninguém está atendendo agora" é diferente de "este
     * cliente não é de ninguém".
     */
    usuario_id: z.string().uuid().nullable(),
    /**
     * Por que está sendo passada.
     *
     * Obrigatório, e com tamanho mínimo. Transferência sem motivo registrado é
     * o tipo de coisa que ninguém consegue reconstruir três meses depois,
     * quando a pergunta for "por que este cliente mudou de vendedor?".
     */
    motivo: z
      .string()
      .trim()
      .min(3, 'Diga em poucas palavras por que está passando — quem ler depois vai precisar.')
      .max(280),
  })
  .strict();

export const ConversaTransferidaSchema = z
  .object({
    responsavel: z.object({ usuario_id: z.string().uuid(), nome: z.string() }).nullable(),
    /**
     * O que efetivamente mudou de dono.
     *
     * Explícito, e não implícito no 200: a conversa pode não ter cliente
     * vinculado nem oportunidade, e nesses casos "não moveu" é o certo — não
     * uma falha. A tela diz o que aconteceu em vez de deixar a pessoa supor.
     */
    moveu: z.object({
      conversa: z.boolean(),
      cliente: z.boolean(),
      oportunidade: z.boolean(),
    }),
  })
  .strict();

export type ResponsavelPossivel = z.infer<typeof ResponsavelPossivelSchema>;
export type ResponsaveisPossiveis = z.infer<typeof ResponsaveisPossiveisSchema>;
